import type { ColumnDef } from '@tanstack/react-table'
import type { Rent } from '@einwert/valuation-api-client'
import { ContentChip, decimalToLocalizedString, isoDateStringToLocalFormat, MonetaryValue } from 'ComponentLibrary'
import { SimpleTableCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableCell'
import { SimpleTableHeadline } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableHeadline'
import { SimpleTableRentCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTenantCells'
import { SimpleTableMonetaryCell, SimpleTableTextCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTextCells'
import type { TenantDetailsTableRow } from 'ComponentLibrary/src/widgets/TenantTables/TenantDetailsTable/types'
import { previewYearlyRent, type PreviewTenant } from './preview'
import { tenantImportCopy, type TenantImportLanguage } from './copy'

export function tenantImportPreviewColumns(
    rows: PreviewTenant[],
    fieldErrors: Record<string, string | undefined>[],
    language: TenantImportLanguage = 'de'
): ColumnDef<TenantDetailsTableRow>[] {
    const copy = tenantImportCopy(language)
    const locale = language === 'en' ? 'en-GB' : 'de-DE'
    const date = (value: string | undefined) => isoDateStringToLocalFormat(value, false, locale) ?? '–'
    const formatRent = (rent: Rent) =>
        [
            MonetaryValue.fromMonetaryValueDataObject(rent.value).format({ locale }),
            copy.paymentScope[rent.payment_scope],
            copy.paymentPeriod[rent.payment_period],
        ].join(' ')
    const textColumn = (id: string, label: string, value: (tenant: PreviewTenant) => string | undefined, size = 220) =>
        rows.some((tenant, index) => value(tenant) || fieldErrors[index][id])
            ? [
                  {
                      id,
                      header: () => <SimpleTableHeadline variant="secondary" text={label} withPadding />,
                      size,
                      cell: ({ row }) => <SimpleTableTextCell text={value(rows[row.original.tenantIndex])} />,
                  } satisfies ColumnDef<TenantDetailsTableRow>,
              ]
            : []

    return [
        {
            id: 'contract_rent',
            header: () => <SimpleTableHeadline variant="secondary" text={copy.contractRent} withPadding />,
            size: 220,
            cell: ({ row }) => (
                <SimpleTableRentCell
                    locale={locale}
                    paymentPeriodLabels={copy.paymentPeriod}
                    paymentScopeLabels={copy.paymentScope}
                    rent={row.original.rental_agreement?.rent}
                    showPaymentPeriod
                    alignment="right"
                />
            ),
        },
        ...(rows.some((tenant) => tenant.market_rent)
            ? [
                  {
                      id: 'market_rent',
                      header: () => <SimpleTableHeadline variant="secondary" text={copy.marketRent} withPadding />,
                      size: 220,
                      cell: ({ row }) => (
                          <SimpleTableRentCell
                              locale={locale}
                              paymentPeriodLabels={copy.paymentPeriod}
                              paymentScopeLabels={copy.paymentScope}
                              rent={rows[row.original.tenantIndex].market_rent}
                              showPaymentPeriod
                              alignment="right"
                          />
                      ),
                  } satisfies ColumnDef<TenantDetailsTableRow>,
              ]
            : []),
        {
            id: 'contract_yearly_rent',
            header: () => <SimpleTableHeadline variant="secondary" text={copy.yearlyRent} withPadding />,
            size: 200,
            cell: ({ row }) => (
                <SimpleTableMonetaryCell
                    monetaryValue={new MonetaryValue(previewYearlyRent(rows[row.original.tenantIndex]))}
                    locale={locale}
                />
            ),
        },
        {
            id: 'lease_type',
            header: () => <SimpleTableHeadline variant="secondary" text={copy.leaseType} withPadding />,
            size: 120,
            cell: ({ row }) => {
                const type = rows[row.original.tenantIndex].rental_agreement?.rental_agreement_type
                return <SimpleTableTextCell text={type ? copy.leaseTypes[type] : undefined} />
            },
        },
        {
            id: 'private_person',
            header: () => <SimpleTableHeadline variant="secondary" text={copy.privatePerson} withPadding />,
            size: 120,
            cell: ({ row }) => {
                const privatePerson = rows[row.original.tenantIndex].rental_agreement?.private_person
                return (
                    <SimpleTableCell>
                        <ContentChip size="small" color={privatePerson ? 'green' : 'grey'} label={privatePerson ? copy.yes : copy.no} />
                    </SimpleTableCell>
                )
            },
        },
        ...textColumn(
            'graduations',
            copy.graduations,
            (tenant) =>
                tenant.rental_agreement?.rental_agreement_type === 'graduated'
                    ? tenant.rental_agreement.graduations.map((step) => `${date(step.date)}: ${formatRent(step.rent)}`).join(' · ')
                    : undefined,
            380
        ),
        ...textColumn(
            'rent_free_periods',
            copy.rentFree,
            (tenant) =>
                tenant.rental_agreement?.rent_free_periods
                    .map((period) => `${date(period.start_date)} – ${date(period.end_date)}`)
                    .join(' · '),
            380
        ),
        ...textColumn(
            'vacancy_periods',
            copy.vacancies,
            (tenant) =>
                tenant.vacancy_periods
                    .map((period) =>
                        [
                            `${date(period.start_date)} – ${date(period.end_date)}`,
                            period.duration !== undefined && `${decimalToLocalizedString(period.duration)} ${copy.months}`,
                        ]
                            .filter(Boolean)
                            .join('; ')
                    )
                    .join(' · '),
            380
        ),
    ]
}
